import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, ValidatorFn, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { BehaviorSubject } from 'rxjs';

import { InputComponent } from '@shared/components/input/input.component';
import { MultiSelectDropdownComponent } from '@shared/components/multi-select-dropdown/multi-select-dropdown.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { ToggleComponent } from '@shared/components/toggle/toggle.component';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { TrialImplementationStatus, TrialPhase } from '@shared/services/gql.service';
import { TrialsQuery } from '@shared/store/trials/trials.query';
import { TrialsService } from '@shared/store/trials/trials.service';
import { Option } from '@shared/types/components.type';
import { Utils } from '@shared/utils/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    InputComponent,
    ToggleComponent,
    NgSelectModule,
    AsyncPipe,
    FormErrorDirective,
    MultiSelectDropdownComponent,
  ],
  selector: 'aux-new-trial-modal',
  styleUrl: './new-trial-dialog.component.scss',
  templateUrl: './new-trial-dialog.component.html',
})
export class NewTrialDialogComponent {
  public ref = inject(CustomOverlayRef<string>);
  public trialsQuery = inject(TrialsQuery);

  private formBuilder = inject(UntypedFormBuilder);
  private trialsService = inject(TrialsService);

  cache: {
    [k: string]: undefined | { data?: string; error?: string; success: boolean };
  } = {};
  customFg = this.formBuilder.group({
    auxilius_start_date: null,
    implementation_status: TrialImplementationStatus.IMPLEMENTATION_STATUS_PENDING,
    indication: '',
    name: '',
    onboarding_complete: true,
    program: null,
    project: null,
    short_name: '',
    sponsor_organization_name: '',
    therapy_area: null,
    trial_phase: null,
    workspace_type: [null, Validators.required],
  });
  implementationStatusOptions = Utils.TRIAL_IMPLEMENTATION_STATUS_OPTIONS;
  loading$ = new BehaviorSubject(false);
  sponsor$ = new BehaviorSubject<undefined | { data?: string; error?: string; success: boolean }>(
    undefined
  );
  therapyAreaOptions: Option[] = [];
  trialPhaseOptions: Option<TrialPhase[]>[] = [
    {
      label: 'Pre Clinical',
      value: [TrialPhase.TRIAL_PHASE_PRE_CLINICAL],
    },
    {
      label: 'Phase 1',
      value: [TrialPhase.TRIAL_PHASE_PHASE_1],
    },
    {
      label: 'Phase 1/2',
      value: [TrialPhase.TRIAL_PHASE_PHASE_1, TrialPhase.TRIAL_PHASE_PHASE_2],
    },
    {
      label: 'Phase 2',
      value: [TrialPhase.TRIAL_PHASE_PHASE_2],
    },
    {
      label: 'Phase 3',
      value: [TrialPhase.TRIAL_PHASE_PHASE_3],
    },
    {
      label: 'Phase 4',
      value: [TrialPhase.TRIAL_PHASE_PHASE_4],
    },
  ];
  trialShortNameValidators: ValidatorFn[] = [
    (control) => {
      const value: string = control.value;

      if (!value) {
        return { required: true };
      }

      return this.trialsQuery
        .getAll()
        .find((trial) => trial.short_name?.toLowerCase() === value.toLowerCase())
        ? { duplicateTrial: true }
        : null;
    },
  ];
  workspaceTypeOptions = Object.entries(Utils.TRIAL_WORKSPACE_TYPE_OPTIONS).map(([key, label]) => ({
    key,
    label,
  }));

  constructor() {
    this.initTherapyAreaOptions().catch(console.error);
  }

  async createCustomTrial() {
    if (this.customFg.valid && !this.loading$.getValue()) {
      this.loading$.next(true);
      const {
        auxilius_start_date,
        implementation_status,
        indication,
        name,
        onboarding_complete,
        program,
        project,
        short_name,
        sponsor_organization_name,
        therapy_area,
        trial_phase,
        workspace_type,
      } = this.customFg.value;

      const resp = await this.trialsService.createCustomTrial({
        auxilius_start_date,
        implementation_status,
        indication,
        name,
        onboarding_complete,
        program,
        project,
        short_name,
        sponsor_organization_name,
        therapy_area,
        trial_phase,
        workspace_type,
      });

      this.loading$.next(false);

      this.ref.close(resp);
    }
  }

  async initTherapyAreaOptions(): Promise<void> {
    this.therapyAreaOptions = await this.trialsService.getTherapyAreaOptions();
  }
}
